import styled from '@emotion/styled'
import Link from 'next/link'

import Badge from 'components/Badge'
import Button from 'components/Button'
import ConfirmationModal from 'components/ConfirmationModal'
import { FeatureExplanation, FeatureItem, FeaturesList } from 'components/FeaturesList'
import { Portal } from 'components/Portal'
import UseAsyncFn from 'components/UseAsyncFn'
import Video from 'components/Video'
import YouTubeVideoModal from 'components/YouTubeVideoModal'
import { ArrowForwardIcon, EnvelopeIcon, RepeatIcon } from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useIntegration } from 'hooks/useIntegration'
import { useTextProductState } from 'hooks/useTextProductState'
import { useToast } from 'hooks/useToast'
import { useTracking } from 'hooks/useTracking'
import { ActionButtonsPortalContext } from 'layouts/DefaultLayout'
import { connectIntegration, disconnectIntegration } from 'lib/api'
import { HelpDeskIntegrationUrls } from 'lib/constants'
import { validateFetchError } from 'lib/fetcher'
import { isMobile } from 'lib/helpers'
import { store, useAppDispatch } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: ${({ theme }) => theme.spaces.space7};
`

const VideoSection = styled.div`
	background-color: ${({ theme }) => theme.colors.light.surfaceVariant};
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
`

const Explanation = styled(FeatureExplanation)`
	margin-bottom: 0;
`

const ButtonColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space4};
`

const StyledButton = styled(Button)`
	padding: ${({ theme }) => theme.spaces.space4};
`

const BaseLink = styled(Link)`
	text-decoration: none;
`

const EnabledBadge = styled(Badge)`
	background-color: ${({ theme }) => theme.colors.light.desaturetedGreen};
	display: flex;
	justify-content: center;
	white-space: normal;
`

function HelpDeskIntegration() {
	const mobile = isMobile()
	const track = useTracking()
	const showToast = useToast()
	const dispatch = useAppDispatch()
	const { integration, mutateIntegration } = useIntegration('helpdesk')
	const { feature: contactFeature } = useFeature('forms', { isPaused: () => !integration?.connected })
	const fallbackEmail = contactFeature?.properties.email
	const helpDeskProductState = useTextProductState('HelpDesk')

	if (!integration) {
		return null
	}

	function showDisconnectModal() {
		dispatch({ type: 'currentModal/set', payload: 'ConfirmDisableModal' })
	}

	function hideDisconnectModal() {
		dispatch({ type: 'currentModal/set', payload: null })
	}

	async function handleConnect() {
		try {
			await connectIntegration('helpdesk', undefined)
			mutateIntegration({ connected: true, installations: [] }, { revalidate: false })
			showToast('success', 'HelpDesk integration connected')
		} catch (error) {
			showToast('error', validateFetchError(error) ? error.data.message : 'HelpDesk integration failed to connect')
		}
	}

	async function handleDisconnect() {
		try {
			await disconnectIntegration('helpdesk')
			mutateIntegration({ connected: false, installations: [] }, { revalidate: false })
			showToast('success', 'HelpDesk integration disconnected')
			hideDisconnectModal()
		} catch (error) {
			showToast('error', validateFetchError(error) ? error.data.message : 'HelpDesk integration failed to disconnect')
		}
	}

	const disconnectButton = (
		<StyledButton
			fullWidth
			type="button"
			kind="destructive-secondary"
			onClick={() => {
				showDisconnectModal()
				track(EVENT_NAMES.HELPDESK_DISCONNECT_CLICKED)
			}}
			disableHover
		>
			Disconnect
		</StyledButton>
	)

	return (
		<Wrapper>
			<YouTubeVideoModal src={HelpDeskIntegrationUrls.PROMO_VIDEO_EMBED} />
			{integration.connected && <EnabledBadge>All messages from OpenWidget are forwarded to HelpDesk.</EnabledBadge>}
			<VideoSection>
				<Video src="/videos/HDIntegration.mp4" />
			</VideoSection>
			<Explanation>
				HelpDesk is a smart ticketing system made to manage all messages from customers in one place. Automate your
				daily workflow and speed up your Customer Service.
			</Explanation>
			<FeaturesList>
				<FeatureItem>
					<ArrowForwardIcon />
					Forward messages from OpenWidget to HelpDesk
				</FeatureItem>
				<FeatureItem>
					<EnvelopeIcon />
					Manage all customer messages in one place
				</FeatureItem>
				<FeatureItem>
					<RepeatIcon />
					Resolve repetitive inquiries automatically
				</FeatureItem>
			</FeaturesList>
			{integration.connected ? (
				<ButtonColumn>
					<BaseLink href={HelpDeskIntegrationUrls.TICKETS} target="_blank" rel="noopener">
						<StyledButton
							fullWidth
							kind="primary"
							type="button"
							onClick={() => track(EVENT_NAMES.HELPDESK_SEE_TICKETS_CLICKED)}
							disableHover
						>
							See HelpDesk tickets
						</StyledButton>
					</BaseLink>
					<BaseLink href={HelpDeskIntegrationUrls.APP_OW_SETTINGS} target="_blank" rel="noopener">
						<StyledButton
							fullWidth
							kind="secondary"
							type="button"
							onClick={() => track(EVENT_NAMES.HELPDESK_GO_TO_SETTINGS_CLICKED)}
							disableHover
						>
							HelpDesk settings
						</StyledButton>
					</BaseLink>
					{mobile ? disconnectButton : <Portal context={ActionButtonsPortalContext}>{disconnectButton}</Portal>}
				</ButtonColumn>
			) : (
				<ButtonColumn>
					<UseAsyncFn key="connect" asyncFn={handleConnect}>
						{({ loading, callAsyncFn }) => (
							<StyledButton
								fullWidth
								kind="primary"
								type="button"
								loading={loading}
								onClick={() => {
									callAsyncFn()
									track(EVENT_NAMES.HELPDESK_TRY_CLICKED)
								}}
								disableHover
							>
								{helpDeskProductState === 'inactive' || helpDeskProductState === 'unknown' ? 'Try' : 'Connect'} HelpDesk
							</StyledButton>
						)}
					</UseAsyncFn>
					{mobile ? (
						<BaseLink href={HelpDeskIntegrationUrls.PROMO_VIDEO} target="_blank" rel="noopener,noreferrer">
							<StyledButton
								fullWidth
								kind="secondary"
								type="button"
								onClick={() => track(EVENT_NAMES.HELPDESK_LEARN_HOW_IT_WORKS_CLICKED)}
								disableHover
							>
								Learn how it works
							</StyledButton>
						</BaseLink>
					) : (
						<StyledButton
							fullWidth
							kind="secondary"
							type="button"
							onClick={() => {
								store.dispatch({ type: 'currentModal/set', payload: 'YouTubeVideoModal' })
								track(EVENT_NAMES.HELPDESK_LEARN_HOW_IT_WORKS_CLICKED)
							}}
							disableHover
						>
							Learn how it works
						</StyledButton>
					)}
				</ButtonColumn>
			)}
			<ConfirmationModal
				hideModal={hideDisconnectModal}
				name="ConfirmDisableModal"
				data-testid="ConfirmDisableModal"
				title="Are you sure you want to disconnect HelpDesk?"
				caption={
					<span>
						After this action we will forward all new messages from widget to <b>{fallbackEmail ?? 'your email'}</b>
					</span>
				}
			>
				<Button kind="secondary" type="button" disableHover onClick={hideDisconnectModal}>
					Keep it
				</Button>
				<UseAsyncFn key="disconnect" asyncFn={handleDisconnect}>
					{({ loading, callAsyncFn }) => (
						<Button disableHover type="button" kind="destructive" loading={loading} onClick={callAsyncFn}>
							Disconnect
						</Button>
					)}
				</UseAsyncFn>
			</ConfirmationModal>
		</Wrapper>
	)
}

export default HelpDeskIntegration
